import React, { useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * AdminQuickBar
 * A discreet, modern floating toolbar displayed on public pages ONLY when
 * an authorized admin is actively logged in. Allows quick jump to dashboard
 * or quick logout without interfering with normal public site UX.
 */
const AdminQuickBar = ({ adminUser, adminRole, onLogout }) => {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div 
      className="fixed bottom-5 right-5 z-50 font-sans transition-all duration-300"
      style={{ fontFamily: "'Montserrat', sans-serif" }}
    >
      {collapsed ? (
        <button
          onClick={() => setCollapsed(false)}
          title="Expand Admin Bar (Shortcut: Ctrl+Shift+A)"
          className="flex items-center gap-2 bg-slate-950/90 text-white hover:text-emerald-400 border border-emerald-500/40 backdrop-blur-md px-3.5 py-2 rounded-full shadow-2xl hover:scale-105 transition-all text-xs font-medium"
        >
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
          </span>
          <svg className="w-3.5 h-3.5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          <span>Admin</span>
        </button>
      ) : (
        <div className="flex items-center gap-2.5 bg-slate-950/95 text-white border border-slate-700/80 backdrop-blur-md px-3.5 py-2 rounded-full shadow-2xl text-xs">
          {/* Active status pulse */}
          <div className="flex items-center gap-1.5 pl-1">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
            </span>
            <span className="text-[11px] font-semibold text-slate-300 hidden sm:inline">
              Staff Active
            </span>
          </div>

          {/* Role badge */}
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wide uppercase ${
            adminRole === 'Super Admin'
              ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
              : 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
          }`}>
            {adminRole || 'Admin'}
          </span>

          <span className="text-slate-600 hidden md:inline">|</span>

          {/* Dashboard Button */}
          <Link
            to="/admin"
            className="flex items-center gap-1 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold px-3 py-1 rounded-full transition-colors shadow-sm"
          >
            <span>Dashboard</span>
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
            </svg>
          </Link>

          {/* Registrations shortcut */}
          <Link
            to="/form"
            title="View Student Registrations"
            className="hidden sm:flex items-center gap-1 bg-slate-800 hover:bg-slate-700 text-slate-200 px-2.5 py-1 rounded-full transition-colors"
          >
            <svg className="w-3 h-3 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span>Submissions</span>
          </Link>

          {/* Logout button */}
          <button
            onClick={onLogout}
            title="Log out from Admin session"
            className="text-slate-400 hover:text-rose-400 p-1 transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
          </button>

          {/* Collapse button */}
          <button
            onClick={() => setCollapsed(true)}
            title="Minimize bar"
            className="text-slate-500 hover:text-slate-300 p-1"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminQuickBar;
